import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';
import { displayError } from '@/lib/error';
import { toast } from '@/components/ui/use-toast';
import { useUserStore } from '@/stores/user';
import { hasFeature } from '@/services/featureService';
import { isAdminOrSupervisor, isDueDiligenceTeamMember, canSelectDdiAssigneeOnForm } from '@/services/userService';
import { useCreateDdiWorkOrder } from '@/services/dueDiligenceService';
import { Feature } from '@/utils/constant';
import { DDIWorkOrderCreateStep1 } from './DDIWorkOrderCreateStep1';
import { DDIWorkOrderCreateStep2 } from './DDIWorkOrderCreateStep2';
import {
  applyZodIssuesToForm,
  buildDdiWorkOrderCreatePayload,
  DdiStep1Schema,
  DdiStep2Schema,
  type DdiCreateFormValues,
} from './ddiWorkOrderCreate.schema';

const STEPS = [
  { title: 'Case Details', isCompleted: false },
  { title: 'Search Details', isCompleted: false },
];

const STEP_SCHEMAS = [DdiStep1Schema, DdiStep2Schema];

export const DDIWorkOrderCreate = () => {
  const navigate = useNavigate();
  const loggedInUser = useUserStore((state) => state.user);
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(STEPS);
  const [currentSchema, setCurrentSchema] = useState<(typeof STEP_SCHEMAS)[number]>(STEP_SCHEMAS[0]);
  /** When true, the next activeStep effect must not clear errors (Save → Step 1 failure). */
  const preserveErrorsOnStepChangeRef = useRef(false);

  const showAssignedUserField =
    hasFeature(Feature.DUE_DILIGENCE_WORK_ORDER_ASSIGN) && canSelectDdiAssigneeOnForm(loggedInUser);

  const form = useForm<DdiCreateFormValues>({
    resolver: zodResolver(currentSchema),
    mode: 'all',
    defaultValues: {
      serviceType: 'Due Diligence',
      ddiType: '',
      ddiTypeId: null,
      ddiSubtype: null,
      ddiSubtypeId: null,
      subtypeRequired: false,
      subtypesLookupPending: false,
      fileNumber: '',
      clientId: null,
      clientCode: '',
      clientName: null,
      contactId: '',
      contactEmail: null,
      lawyerId: null,
      courtName: null,
      caseType: '',
      caseSubType: '',
      caseNumber: '',
      caseName: '',
      personForInquiry: null,
      entityForInquiry: null,
      vin: null,
      addressType: null,
      addressLine1: null,
      addressLine2: null,
      city: null,
      state: null,
      zipCode: null,
      showAlternativeAddress: false,
      alternativeAddressLine1: null,
      alternativeAddressLine2: null,
      alternativeAddressCity: null,
      alternativeAddressState: null,
      alternativeAddressZipCode: null,
      notes: null,
      sameDayRush: false,
      nextDayRush: false,
      dueDate: null,
      dueDateTouched: false,
      reportId: null,
      assignedUserId: null,
    },
  });

  const subtypesLookupPending = form.watch('subtypesLookupPending');

  useEffect(() => {
    setCurrentSchema(STEP_SCHEMAS[activeStep]);
    const preserveErrors = preserveErrorsOnStepChangeRef.current;
    preserveErrorsOnStepChangeRef.current = false;
    setTimeout(async () => {
      await form.trigger();
      if (!preserveErrors) {
        form.clearErrors();
      }
    }, 50);
  }, [activeStep]);

  const scrollToTop = () => {
    const el = document.getElementById('toolbar');
    if (el) el.scrollIntoView({ block: 'end', behavior: 'smooth' });
  };

  const nextStep = async () => {
    if (form.getValues('subtypesLookupPending')) return;
    const isValid = await form.trigger();
    if (isValid) {
      setSteps((prev) => prev.map((s, i) => (i === activeStep ? { ...s, isCompleted: true } : s)));
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      setTimeout(scrollToTop, 100);
    }
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    setTimeout(scrollToTop, 100);
  };

  const { mutate: createWorkOrder, status: createStatus } = useCreateDdiWorkOrder();

  const canAccess =
    hasFeature(Feature.DUE_DILIGENCE) &&
    hasFeature(Feature.DUE_DILIGENCE_WORK_ORDER_CREATE) &&
    (isDueDiligenceTeamMember(loggedInUser) || isAdminOrSupervisor(loggedInUser));

  if (!canAccess) {
    return <Navigate to="/not-found" />;
  }

  const saveHandler = () => {
    const values = form.getValues();
    const step1Result = DdiStep1Schema.safeParse(values);
    const step2Result = DdiStep2Schema.safeParse(values);

    if (!step1Result.success) {
      applyZodIssuesToForm(step1Result.error.issues, form.setError);
      preserveErrorsOnStepChangeRef.current = true;
      setActiveStep(0);
      setTimeout(scrollToTop, 100);
      return;
    }

    if (!step2Result.success) {
      applyZodIssuesToForm(step2Result.error.issues, form.setError);
      return;
    }

    const payload = buildDdiWorkOrderCreatePayload(values);

    createWorkOrder(payload, {
      onSuccess: () => {
        toast({
          variant: 'default',
          description: 'Due Diligence work order created successfully.',
          duration: 5000,
        });
        navigate('/due-diligence');
      },
      onError: () => {
        displayError(
          { error: { message: 'Failed to create Due Diligence work order' } },
          5000
        );
      },
    });
  };

  return (
    <div className="w-full scrollbar-hide">
      <div className="w-full">
        <ToolBar title="Due Diligence Work Order Entry" />
        <div className="mt-3 flex justify-center pl-24">
          <Stepper steps={steps} activeStep={activeStep} goToStep={Function.prototype as (step: number) => void} />
        </div>

        <FormProvider {...form}>
          {activeStep === 0 && <DDIWorkOrderCreateStep1 />}
          {activeStep === 1 && <DDIWorkOrderCreateStep2 showAssignedUserField={showAssignedUserField} />}
        </FormProvider>

        <div className="flex justify-end mt-4 mb-8 w-full gap-2">
          {activeStep > 0 && (
            <Button
              variant="outline"
              className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
              onClick={prevStep}
            >
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 && (
            <Button
              className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white disabled:bg-primary"
              onClick={nextStep}
              disabled={!form.formState.isValid || !!subtypesLookupPending}
            >
              Next
            </Button>
          )}
          {activeStep === steps.length - 1 && (
            <Button
              className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white"
              onClick={saveHandler}
              disabled={!form.formState.isValid || createStatus === 'pending'}
            >
              Save
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DDIWorkOrderCreate;
